import React from 'react';

import PropTypes from 'prop-types';

import { Button, Sheet } from '@iris/react-components';

import {
    SubHeading,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { inventoryViewAllFeaturesClick } from 'reaxl-analytics-handlers';

import FeatureItem from './FeatureItem';
import PackageItem from './PackageItem';

const removeNewUsedFromTitle = (title) => {
    if (!title || typeof title !== 'string') {
        return title;
    }

    // Remove "New " or "Used " from the beginning of the title (case insensitive)
    return title.replace(/^(new|used)\s+/i, '').trim();
};

/**
 * VehicleFeaturesSheet displays vehicle features and packages in a Sheet modal dialog.
 * This component uses the Iris Sheet component to show detailed information about vehicle features
 * organized by categories and any available packages with their included features.
 *
 * The Sheet component provides a modern modal experience with a trigger button that opens
 * the detailed view when clicked.
 *
 * @param {Object} props - Component props
 * @param {Array} props.packages - Array of package objects containing package information and features
 * @param {Object} props.features - Object with feature categories as keys and arrays of features as values
 * @param {string} props.vehicleTitle - The title of the vehicle (will be cleaned of "New"/"Used" prefixes)
 * @returns {React.ReactElement} The rendered Sheet component with vehicle features content
 */

export default function VehicleFeaturesSheet({ packages, features, vehicleTitle }) {
    const hasPackages = !!packages?.length;
    const cleanedVehicleTitle = removeNewUsedFromTitle(vehicleTitle);

    const packagesSection = hasPackages && (
        <div key="packages-section">
            <SubHeading
                size="sm"
                className="padding-top-4 padding-bottom-4"
                level={3}
            >
                Packages
            </SubHeading>
            {packages.map((item, index) => (
                <PackageItem
                    item={item}
                    key={`package-${index}`}
                    context="modal"
                />
            ))}
        </div>
    );

    const featuresSection = features && Object.keys(features).length && (
        <div key="features-section">
            {Object.entries(features).map(([feature, featureList], index) => (
                <FeatureItem
                    key={`${feature}-${index}`}
                    feature={feature}
                    featureList={featureList}
                    context="modal"
                    hasPackages={hasPackages}
                    index={index}
                />
            ))}
        </div>
    );

    const handleViewFeaturesClick = () => {
        sendClick(inventoryViewAllFeaturesClick);
    };

    const vehicleTitleSection = (
        <div className="iris:border-b-1 iris:self-start iris:pb-4 iris:w-full">
            <SubHeading
                size="500"
                className="align-self-start"
            >
                {cleanedVehicleTitle}
            </SubHeading>
        </div>
    );

    return (
        <div data-cmp="vehicleFeaturesSheet">
            <Sheet
                aria-label=""
                desktopPosition="center"
                modal
                title="Vehicle Features"
                trigger={(
                    <Button
                        variant="tertiary"
                        size="md"
                        onClick={handleViewFeaturesClick}
                    >
                        View All Features
                    </Button>
                )}
            >
                {/* temporary fix until conflicting atcui and iris styles resolved */}
                <style>
                    {
                        `button[aria-label="close"][class*="iris:"] {
                                margin: 8px;
                            }
                        }`
                    }
                </style>
                <div className="iris:w-full iris:min-md:w-[600px] iris:min-lg:w-[900px]">
                    {vehicleTitleSection}
                    {packagesSection}
                    {featuresSection}
                </div>
            </Sheet>
        </div>
    );
}

VehicleFeaturesSheet.propTypes = {
    /** Array of package objects containing package information and features */
    packages: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                name: PropTypes.string,
                details: PropTypes.string,
            }),
        ])
    ),
    /** Object with feature categories as keys and arrays of features as values */
    features: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.string)
    ),
    /** The title of the vehicle (will be cleaned of "New"/"Used" prefixes) */
    vehicleTitle: PropTypes.string,
};

VehicleFeaturesSheet.defaultProps = {
    packages: [],
    features: {},
    vehicleTitle: '',
};
